import React, { useEffect, useState } from "react";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Avatar, Descriptions, Spin, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { deleteProjectById, fetchAllProjects } from "../../app/projectSlice";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import CommonModal from "../../components/CommonModal";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../utils/permission";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import SortIcons from "../../components/SortIcons/SortIcons";

const ProjectList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { projectList, total, loading } = useSelector(
    (state: RootState) => state.project
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [page, setPage] = useState(1);
  const [pagesize] = useState(10);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [orderBy, setOrderBy] = useState<
    "id" | "name" | "start" | "end" | "status"
  >("id");
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const loadProjects = async () => {
      try {
        await dispatch(
          fetchAllProjects({
            page,
            pagesize,
            search: debouncedSearch,
            order_by: orderBy,
            order_dir: orderDir,
          })
        ).unwrap();

        //  toast.success(result.message || "Projects fetched successfully");
      } catch (error: any) {
        toast.error(error?.message || "Failed to fetch projects");
      }
    };

    loadProjects();
  }, [dispatch, page, pagesize, debouncedSearch, orderBy, orderDir]);
  const handleDeleteConfirm = async (id: number) => {
    try {
      const result = await dispatch(deleteProjectById(id)).unwrap();
      setPage(1);
      return result.message || "Survey deleted successfully";
    } catch (error: any) {
      toast.error(error || "Failed to delete project");
    }
  };

  const handleSort = (field: typeof orderBy) => {
    if (orderBy === field) {
      setOrderDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setOrderBy(field);
      setOrderDir("desc");
    }
    setPage(1);
  };
  return (
    <>
      <Breadcrumb pageName="Manage Project" />
      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-md dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <div className="max-w-full overflow-x-auto">
          {/* Search Input */}
          <div className="flex justify-end mb-4">
            <input
              type="text"
              placeholder="Search by project name"
              value={search}
              onChange={(e) => {
                const value = e.target.value.replace(/^\s+/, "");
                setSearch(value);
                setPage(1);
              }}
              className="w-full sm:w-1/3 rounded-md border border-gray-300 px-4 py-2 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring focus:ring-blue-200 focus:outline-none"
            />
          </div>

          <table className="w-full table-auto sm:table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left">Sr No</th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-2">
                    <span>Name</span>
                    <SortIcons
                      isActive={orderBy === "name"}
                      direction={orderDir}
                    />
                  </div>
                </th>

                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("start")}
                >
                  <div className="flex items-center gap-2">
                    <span>Start</span>
                    <SortIcons
                      isActive={orderBy === "start"}
                      direction={orderDir}
                    />
                  </div>
                </th>

                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("end")}
                >
                  <div className="flex items-center gap-2">
                    <span>End</span>
                    <SortIcons
                      isActive={orderBy === "end"}
                      direction={orderDir}
                    />
                  </div>
                </th>

                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-2">
                    <span>Status</span>
                    <SortIcons
                      isActive={orderBy === "status"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th className="py-4 px-4 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : projectList.length > 0 ? (
                projectList.map((project: any, index: number) => (
                  <tr key={project.id}>
                    <td className="border-b py-4 px-4 ">
                      {(page - 1) * pagesize + index + 1}
                    </td>
                    <td className="border-b py-4 px-4">
                      {project.name || "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {project.start
                        ? new Date(project.start).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      {project.end
                        ? new Date(project.end).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4">
                      <Tag
                        color={
                          project.status?.toLowerCase() === "active"
                            ? "green"
                            : project.status?.toLowerCase() === "inactive"
                            ? "red"
                            : "default"
                        }
                        className="uppercase font-medium"
                      >
                        {project.status ? project.status.toUpperCase() : "--"}
                      </Tag>
                    </td>
                    <td className="border-b py-4 px-4">
                      <div className="flex gap-2">
                        {hasPermission("project-view") && (
                          <button
                            onClick={() => {
                              setSelectedProject(project);
                              setIsModalVisible(true);
                            }}
                          >
                            <EyeOutlined />
                          </button>
                        )}
                        {hasPermission("project-update") && (
                          <button
                            className="hover:text-green-600"
                            onClick={() =>
                              navigate("/dashboard/projectUpdate", {
                                state: {
                                  id: project.id,
                                },
                              })
                            }
                          >
                            <EditOutlined />
                          </button>
                        )}
                        {hasPermission("project-delete") && (
                          <button
                            className="hover:text-red-500"
                            onClick={async () => {
                              const result = await Swal.fire({
                                title:
                                  "Are you sure you want to delete this project?",
                                text: "You won't be able to revert this!",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#d33",
                                cancelButtonColor: "#3085d6",
                                confirmButtonText: "Yes, delete it!",
                              });

                              if (result.isConfirmed) {
                                try {
                                  const message = await handleDeleteConfirm(
                                    project.id
                                  );
                                  await Swal.fire({
                                    title: "Deleted!",
                                    text:
                                      message || "Project has been deleted.",
                                    icon: "success",
                                    timer: 1500,
                                    showConfirmButton: false,
                                  });
                                } catch (err: any) {
                                  await Swal.fire({
                                    title: "Error",
                                    text:
                                      err?.message ||
                                      "Failed to delete project",
                                    icon: "error",
                                  });
                                }
                              }
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {projectList && projectList.length > 0 && (
            <div className="flex justify-between items-center mt-4 mb-2 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pagesize + 1} to{" "}
                {Math.min(page * pagesize, total)} of {total} projects
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {/* Previous Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  &lt;
                </button>

                {/* Page Numbers with Ellipsis */}
                {[...Array(Math.ceil(total / pagesize)).keys()]
                  .map((n: number) => n + 1)
                  .filter((p: number) => {
                    return (
                      p === 1 ||
                      p === Math.ceil(total / pagesize) ||
                      (p >= page - 1 && p <= page + 1)
                    );
                  })
                  .reduce((acc: (number | string)[], val: number, i, arr) => {
                    if (i > 0 && val - (arr[i - 1] as number) > 1) {
                      acc.push("...");
                    }
                    acc.push(val);
                    return acc;
                  }, [])
                  .map((p: number | string, idx: number) =>
                    typeof p === "string" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          p === page
                            ? "bg-gray-300 text-gray-800"
                            : "hover:bg-blue-100 text-blue-600 border-blue-300"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page * pagesize >= total
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page * pagesize >= total}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        <CommonModal
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedProject(null);
          }}
          title={
            <div className="flex items-center gap-2">
              <Avatar icon={<EyeOutlined />} />
              <span>Project Details</span>
            </div>
          }
        >
          {!selectedProject ? (
            <div className="text-center py-6">
              <Spin />
            </div>
          ) : (
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: 600,
                backgroundColor: "#f9f9f9",
                width: "140px",
              }}
              contentStyle={{ backgroundColor: "#fff", color: "#262626" }}
            >
              {[
                { key: "name", label: "Name" },
                { key: "start", label: "Start" },
                { key: "end", label: "End" },
                { key: "vessel_name", label: "Vessel Name" },
                { key: "vessel_owner", label: "Vessel Owner" },
                {
                  key: "vessel_representative",
                  label: "Vessel Representative",
                },
                { key: "pni", label: "P&I Club" },
                { key: "pni_representative", label: "P&I Representative" },
                { key: "consultant", label: "Consultant" },
                { key: "status", label: "Status", isStatus: true },
              ].map(({ key, label, isStatus }) => (
                <Descriptions.Item key={key} label={label}>
                  {selectedProject[key] ? (
                    isStatus ? (
                      <Tag
                        color={
                          selectedProject[key].toLowerCase() === "active"
                            ? "green"
                            : selectedProject[key].toLowerCase() === "inactive"
                            ? "red"
                            : "default"
                        }
                        className="font-medium"
                      >
                        {selectedProject[key].toUpperCase()}
                      </Tag>
                    ) : key === "start" || key === "end" ? (
                      new Date(selectedProject[key]).toLocaleDateString("en-GB") // ✅ dd/mm/yyyy
                    ) : (
                      selectedProject[key]
                    )
                  ) : (
                    "--"
                  )}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}
        </CommonModal>
      </div>
    </>
  );
};

export default ProjectList;
